import React from 'react';
import { Box, Typography, Grid, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IEvent } from '../../types/global/helper';

interface EventScheduleProps {
    event: IEvent;
}

export const EventSchedule: React.FC<EventScheduleProps> = ({ event }) => {
    const { themeStyle } = useThemeContext();

    if (!event.event_date_breakdown || event.event_date_breakdown.length === 0) {
        return (
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                    color: theme[themeStyle].light_gray_text,
                    fontStyle: 'italic',
                }}>
                No schedule items added yet
            </Typography>
        );
    }

    return (
        <Grid container spacing={2}>
            {event.event_date_breakdown.map((breakdown, index) => (
                <Grid item xs={12} key={index}>
                    <Box
                        sx={{
                            p: 2,
                            border: `1px solid ${theme[themeStyle].form_black}`,
                            borderRadius: 1,
                            backgroundColor: theme[themeStyle].gray_text,
                        }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].light_gray_text,
                                        mb: 1,
                                    }}>
                                    Start Date
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    {new Date(breakdown.start_date).toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].light_gray_text,
                                        mb: 1,
                                    }}>
                                    End Date
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    {new Date(breakdown.end_date).toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </Typography>
                            </Grid>
                            {breakdown.detail && (
                                <Grid item xs={12}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].light_gray_text,
                                            mb: 1,
                                        }}>
                                        Details
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        {breakdown.detail}
                                    </Typography>
                                </Grid>
                            )}
                            {breakdown.location?.address && (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 1,
                                        }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].light_gray_text,
                                            }}>
                                            Location
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: theme[themeStyle].placeholderGray,
                                            }}>
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        {breakdown.location.address}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Grid>
            ))}
        </Grid>
    );
};
